import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { v4 as uuidv4 } from 'uuid';
import { currencies } from '../utils/currencies';
import countries from '../utils/countries';
import { FolderZip, Upload, CheckCircle, Error, Close } from '@mui/icons-material';

export default function UploadListing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [zipUploadState, setZipUploadState] = useState({
    uploading: false,
    progress: 0,
    error: null,
    success: false,
    file: null
  });
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    title: '',
    description: '',
    currency: 'USD',
    price: '',
    country: '',
    address: '',
    contactPhone: '',
    images: [],
    zipFile: null,
    revenueType: '',
    annualRevenue: '',
    annualExpense: '',
    viewCount: 0,
    specificFields: {},
  });
  const [errors, setErrors] = useState({});

  // New Categories and Subcategories for Digital Products Marketplace
  const categories = {
    'Mobile Apps': [
      'Social network app', 'Art & Design', 'Auto & Vehicles', 'Beauty', 'Books & Reference',
      'Business', 'Comics', 'Communication', 'Dating', 'Education', 'Entertainment',
      'Events', 'Finance', 'Food & Drink', 'Health & Fitness', 'House & Home',
      'Libraries & Demo', 'Lifestyle', 'Maps & Navigation', 'Medical', 'Music & Audio',
      'News & Magazines', 'Parenting', 'Personalization', 'Photography', 'Productivity',
      'Shopping', 'Social', 'Sports', 'Tools', 'Travel & Local', 'Video Players',
      'Weather', 'Games', 'Multivendor', 'Admin Dashboard', 'Clones', 'Streaming',
      'SaaS', 'Landing Pages'
    ],
    'Web Apps': [
      'Social network app', 'Art & Design', 'Auto & Vehicles', 'Beauty', 'Books & Reference',
      'Business', 'Comics', 'Communication', 'Dating', 'Education', 'Entertainment',
      'Events', 'Finance', 'Food & Drink', 'Health & Fitness', 'House & Home',
      'Libraries & Demo', 'Lifestyle', 'Maps & Navigation', 'Medical', 'Music & Audio',
      'News & Magazines', 'Parenting', 'Personalization', 'Photography', 'Productivity',
      'Shopping', 'Social', 'Sports', 'Tools', 'Travel & Local', 'Video Players',
      'Weather', 'Games', 'Multivendor', 'Admin Dashboard', 'Clones', 'Streaming',
      'SaaS', 'Landing Pages'
    ],
    'Brand Startups': [
      'Social network app', 'Art & Design', 'Auto & Vehicles', 'Beauty', 'Books & Reference',
      'Business', 'Comics', 'Communication', 'Dating', 'Education', 'Entertainment',
      'Events', 'Finance', 'Food & Drink', 'Health & Fitness', 'House & Home',
      'Libraries & Demo', 'Lifestyle', 'Maps & Navigation', 'Medical', 'Music & Audio',
      'News & Magazines', 'Parenting', 'Personalization', 'Photography', 'Productivity',
      'Shopping', 'Social', 'Sports', 'Tools', 'Travel & Local', 'Video Players',
      'Weather', 'Games', 'Multivendor', 'Admin Dashboard', 'Clones', 'Streaming',
      'SaaS', 'Landing Pages'
    ],
    'UI/UX Kits': [
      'Flutter UI Kits', 'React UI Kits', 'Web UI Kits', 'Mobile UI Kits',
      'Admin UI Kits', 'Dashboard UI Kits'
    ],
    'Backend Systems': [
      'Firebase Backend', 'Node.js Backend', 'Django Backend', 'Laravel Backend',
      'Authentication Systems', 'API Systems'
    ],
    'AI & Automation Tools': [
      'AI Chatbots', 'AI SaaS Tools', 'Automation Scripts', 'GPT Integrations',
      'AI Image Generators'
    ],
    'Programming Courses': [
      'Flutter Development', 'React Native Development', 'Android Development',
      'iOS Development', 'JavaScript', 'React / Next.js', 'HTML & CSS',
      'Tailwind CSS', 'Node.js', 'Python', 'Django', 'Laravel', 'Firebase',
      'AI Development', 'Machine Learning', 'API Development', 'Full Stack Development',
      'DevOps'
    ],
    'Graphic designs': [
      'Drawing & Illustration', 'Vector Graphics', 'Photo Editing & Filters',
      'Layout & Desktop Publishing', 'UI & Web Design', 'Templates & Mockups',
      'Logo Makers', 'Typography & Font Tools', 'Color Tools', 'AI Design Generators',
      'Asset Libraries'
    ]
  };

  // Category-Specific Fields Configuration
  const specificFieldsConfig = {
    'Mobile Apps': [
      {
        name: "framework",
        label: "Framework",
        type: "select",
        options: [
          "Flutter", "React Native", "Native Android (Java/Kotlin)", "Native iOS (Swift)",
          "Ionic", "Xamarin", "Others"
        ]
      },
      {
        name: "programmingLanguage",
        label: "Programming Language",
        type: "select",
        options: [
          "Dart", "Kotlin", "Java", "Swift", "Objective-C", "C", "C++", "Rust"
        ]
      },
      {
        name: "backendTechnology",
        label: "Backend Technology",
        type: "select",
        options: [
          "Firebase", "Node.js", "Python", "PHP", "Java", "C#", "Go", "Django",
          "Laravel", "Supabase", "Custom API", "No Backend", "Others"
        ]
      },
      {
        name: "database",
        label: "Database",
        type: "select",
        options: [
          "Firestore", "Realtime Database", "MySQL", "PostgreSQL", "MongoDB", "SQLite", "Others"
        ]
      },
      {
        name: "supportedPlatforms",
        label: "Supported Platforms",
        type: "multi-select",
        options: ["Android", "iOS", "Web", "Desktop"]
      },
      {
        name: "uiFramework",
        label: "UI Framework",
        type: "select",
        options: ["Material UI", "Cupertino UI", "Custom UI", "Tailwind UI", "Others"]
      },
      {
        name: "authenticationMethods",
        label: "Authentication Methods",
        type: "multi-select",
        options: [
          "Email & Password", "Phone OTP", "Google Login", "Facebook Login",
          "Apple Login", "Biometric Login", "Others"
        ]
      },
      {
        name: "paymentIntegrations",
        label: "Payment Integrations",
        type: "multi-select",
        options: [
          "Stripe", "PayPal", "Flutterwave", "Paystack", "Razorpay",
          "In-App Purchases", "Google Pay", "Mobile transactions", "Others", "Not specified"
        ]
      },
      {
        name: "features",
        label: "Features",
        type: "multi-select",
        options: [
          "Push Notifications", "Chat System", "Video Calls", "Live Streaming",
          "Maps Integration", "Dark Mode", "Offline Support", "Admin Panel", "Others"
        ]
      },
      {
        name: "installationSupport",
        label: "Installation Support",
        type: "select",
        options: ["Included", "Not Included", "Paid Support Available"]
      },
      {
        name: "documentation",
        label: "Documentation",
        type: "select",
        options: ["Well Documented", "Basic Documentation", "No Documentation"]
      }
    ],
    'Web Apps': [
      {
        name: "framework",
        label: "Framework",
        type: "select",
        options: [
          "React", "Next.js", "Vue.js", "Angular", "HTML / CSS / JavaScript",
          "Laravel", "Django", "Node.js", "WordPress", "Others"
        ]
      },
      {
        name: "programmingLanguage",
        label: "Programming Language",
        type: "select",
        options: [
          "JavaScript", "TypeScript", "HTML", "CSS", "C", "C++", "Rust"
        ]
      },
      {
        name: "backendTechnology",
        label: "Backend Technology",
        type: "select",
        options: [
          "Firebase", "Node.js", "Python", "PHP", "Java", "C#", "Go", "Django",
          "Laravel", "Supabase", "Custom API", "No Backend", "Others"
        ]
      },
      {
        name: "database",
        label: "Database",
        type: "select",
        options: [
          "Firestore", "Realtime Database", "MySQL", "PostgreSQL", "MongoDB", "SQLite", "Others"
        ]
      },
      {
        name: "supportedPlatforms",
        label: "Supported Platforms",
        type: "multi-select",
        options: ["Web", "Mobile", "Desktop"]
      },
      {
        name: "uiFramework",
        label: "UI Framework",
        type: "select",
        options: ["Material UI", "Tailwind UI", "Bootstrap", "Custom UI", "Others"]
      },
      {
        name: "authenticationMethods",
        label: "Authentication Methods",
        type: "multi-select",
        options: [
          "Email & Password", "Phone OTP", "Google Login", "Facebook Login",
          "Apple Login", "Biometric Login", "Others"
        ]
      },
      {
        name: "paymentIntegrations",
        label: "Payment Integrations",
        type: "multi-select",
        options: [
          "Stripe", "PayPal", "Flutterwave", "Paystack", "Razorpay",
          "Others", "Not specified"
        ]
      },
      {
        name: "features",
        label: "Features",
        type: "multi-select",
        options: [
          "Push Notifications", "Chat System", "Video Calls", "Live Streaming",
          "Maps Integration", "Dark Mode", "Offline Support", "Admin Panel", "Others"
        ]
      },
      {
        name: "installationSupport",
        label: "Installation Support",
        type: "select",
        options: ["Included", "Not Included", "Paid Support Available"]
      },
      {
        name: "documentation",
        label: "Documentation",
        type: "select",
        options: ["Well Documented", "Basic Documentation", "No Documentation"]
      }
    ],
    'Brand Startups': [
      {
        name: "framework",
        label: "Framework",
        type: "select",
        options: [
          "React", "Next.js", "Vue.js", "Angular", "HTML / CSS / JavaScript",
          "Laravel", "Django", "Node.js", "WordPress", "Flutter", "React Native",
          "Native Android", "Native iOS", "Others"
        ]
      },
      {
        name: "programmingLanguage",
        label: "Programming Language",
        type: "select",
        options: [
          "JavaScript", "TypeScript", "HTML", "CSS", "Dart", "Kotlin", "Java",
          "Swift", "Python", "PHP", "C", "C++", "Rust"
        ]
      },
      {
        name: "backendTechnology",
        label: "Backend Technology",
        type: "select",
        options: [
          "Firebase", "Node.js", "Python", "PHP", "Java", "C#", "Go", "Django",
          "Laravel", "Supabase", "Custom API", "No Backend", "Others"
        ]
      },
      {
        name: "database",
        label: "Database",
        type: "select",
        options: [
          "Firestore", "Realtime Database", "MySQL", "PostgreSQL", "MongoDB", "SQLite", "Others"
        ]
      },
      {
        name: "supportedPlatforms",
        label: "Supported Platforms",
        type: "multi-select",
        options: ["Android", "iOS", "Web", "Desktop"]
      },
      {
        name: "uiFramework",
        label: "UI Framework",
        type: "select",
        options: ["Material UI", "Cupertino UI", "Custom UI", "Tailwind UI", "Bootstrap", "Others"]
      },
      {
        name: "authenticationMethods",
        label: "Authentication Methods",
        type: "multi-select",
        options: [
          "Email & Password", "Phone OTP", "Google Login", "Facebook Login",
          "Apple Login", "Biometric Login", "Others"
        ]
      },
      {
        name: "paymentIntegrations",
        label: "Payment Integrations",
        type: "multi-select",
        options: [
          "Stripe", "PayPal", "Flutterwave", "Paystack", "Razorpay",
          "In-App Purchases", "Google Pay", "Mobile transactions", "Others", "Not specified"
        ]
      },
      {
        name: "features",
        label: "Features",
        type: "multi-select",
        options: [
          "Push Notifications", "Chat System", "Video Calls", "Live Streaming",
          "Maps Integration", "Dark Mode", "Offline Support", "Admin Panel", "Others"
        ]
      },
      {
        name: "installationSupport",
        label: "Installation Support",
        type: "select",
        options: ["Included", "Not Included", "Paid Support Available"]
      },
      {
        name: "documentation",
        label: "Documentation",
        type: "select",
        options: ["Well Documented", "Basic Documentation", "No Documentation"]
      }
    ],
    'UI/UX Kits': [
      {
        name: "designTool",
        label: "Design Tool",
        type: "select",
        options: ["Figma", "Adobe XD", "Sketch", "Photoshop"]
      },
      {
        name: "kitType",
        label: "Kit Type",
        type: "select",
        options: ["Mobile UI Kit", "Web UI Kit", "Dashboard UI Kit", "Wireframe Kit"]
      },
      {
        name: "designStyle",
        label: "Design Style",
        type: "select",
        options: ["Modern", "Minimal", "Dark Mode", "Material Design"]
      },
      {
        name: "responsive",
        label: "Responsive Design",
        type: "select",
        options: ["Yes", "No"]
      }
    ],
    'Backend Systems': [
      {
        name: "programmingLanguage",
        label: "Programming Language",
        type: "select",
        options: ["Node.js", "Python", "Java", "Go", "PHP", "C#", "Ruby"]
      },
      {
        name: "framework",
        label: "Framework",
        type: "select",
        options: [
          "Express.js", "NestJS", "Django", "Flask", "Spring Boot",
          "Laravel", "ASP.NET", "Ruby on Rails", "Gin", "FastAPI"
        ]
      },
      {
        name: "database",
        label: "Database",
        type: "select",
        options: [
          "MongoDB", "PostgreSQL", "MySQL", "Firebase", "Supabase", "SQLite", "Redis"
        ]
      },
      {
        name: "apiType",
        label: "API Type",
        type: "select",
        options: ["REST API", "GraphQL", "gRPC"]
      },
      {
        name: "authentication",
        label: "Authentication",
        type: "select",
        options: ["JWT", "OAuth", "Session", "Firebase Auth", "Custom Auth"]
      }
    ],
    'AI & Automation Tools': [
      {
        name: "programmingLanguage",
        label: "Programming Language",
        type: "select",
        options: ["Python", "JavaScript", "TypeScript", "Go", "Java", "C#"]
      },
      {
        name: "framework",
        label: "AI Framework / Library",
        type: "select",
        options: [
          "TensorFlow", "PyTorch", "LangChain", "OpenAI API",
          "Hugging Face", "Custom AI"
        ]
      },
      {
        name: "aiType",
        label: "AI Type",
        type: "select",
        options: [
          "Chatbot", "Image Generation", "Text Generation", "Recommendation System",
          "Automation Bot", "Voice AI", "Computer Vision"
        ]
      },
      {
        name: "integration",
        label: "Integration",
        type: "select",
        options: [
          "REST API", "Web App", "Mobile App", "Browser Extension",
          "Telegram Bot", "Discord Bot"
        ]
      },
      {
        name: "modelAccess",
        label: "Model Access",
        type: "select",
        options: [
          "API Based", "Self Hosted", "Pretrained Model", "Custom Trained Model"
        ]
      }
    ],
    'Programming Courses': [
      {
        name: "language",
        label: "Programming Language",
        type: "select",
        options: [
          "JavaScript", "TypeScript", "Python", "Dart", "Java", "Kotlin",
          "Swift", "C#", "C++", "Go", "Rust"
        ]
      },
      {
        name: "level",
        label: "Skill Level",
        type: "select",
        options: ["Beginner", "Intermediate", "Advanced"]
      },
      {
        name: "courseType",
        label: "Course Type",
        type: "select",
        options: ["Video Course", "Text Course", "Project Based", "Bootcamp"]
      },
      {
        name: "duration",
        label: "Course Duration",
        type: "select",
        options: ["1-2 Hours", "3-5 Hours", "6-10 Hours", "10+ Hours"]
      }
    ],
    'Graphic designs': [
      {
        name: "designType",
        label: "Design Type",
        type: "select",
        options: [
          "Logo", "Social Media Post", "Poster", "Flyer", "Business Card",
          "Brand Identity Kit", "Banner", "Presentation Template", "UI Graphics"
        ]
      },
      {
        name: "softwareUsed",
        label: "Design Software",
        type: "select",
        options: [
          "Adobe Photoshop", "Adobe Illustrator", "Figma", "Adobe XD",
          "Canva", "Sketch", "Other"
        ]
      },
      {
        name: "fileFormat",
        label: "File Format",
        type: "select",
        options: ["PSD", "AI", "FIG", "XD", "SVG", "PNG", "JPG", "PDF"]
      },
      {
        name: "editable",
        label: "Editable Source Files",
        type: "select",
        options: ["Yes", "No"]
      }
    ]
  };

  // Format phone number for display
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const cleaned = value.replace(/(?!^\+)[^0-9]/g, '');
    const match = cleaned.match(/^(\+?\d{0,4})(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      const parts = [match[1], match[2], match[3], match[4]].filter(Boolean);
      return parts.join(' ').trim();
    }
    return cleaned;
  };

  // Clean phone number for storage
  const cleanPhoneNumber = (value) => {
    return value.replace(/\s/g, '');
  };

  // Handle ZIP file upload with simulated progress
  const handleZipUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's a zip file
    if (!file.name.endsWith('.zip') && !file.type.includes('zip')) {
      setZipUploadState({
        uploading: false,
        progress: 0,
        error: 'Please upload a valid ZIP file',
        success: false,
        file: null
      });
      return;
    }

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setZipUploadState({
        uploading: false,
        progress: 0,
        error: 'File size must be less than 100MB',
        success: false,
        file: null
      });
      return;
    }

    setZipUploadState({
      uploading: true,
      progress: 0,
      error: null,
      success: false,
      file: file
    });

    // Simulate upload progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setZipUploadState(prev => ({
        ...prev,
        progress: progress
      }));

      if (progress >= 100) {
        clearInterval(interval);
        setZipUploadState({
          uploading: false,
          progress: 100,
          error: null,
          success: true,
          file: file
        });
        setFormData(prev => ({
          ...prev,
          zipFile: file
        }));
        toast.success('ZIP file uploaded successfully!');
      }
    }, 300);
  };

  // Remove ZIP file
  const removeZipFile = () => {
    setZipUploadState({
      uploading: false,
      progress: 0,
      error: null,
      success: false,
      file: null
    });
    setFormData(prev => ({
      ...prev,
      zipFile: null
    }));
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file' && name === 'images') {
      const newFiles = Array.from(files);
      if (newFiles.length + formData.images.length > 50) {
        toast.error('Maximum 50 images allowed');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newFiles],
      }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (name.startsWith('specificFields.')) {
      const fieldName = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        specificFields: {
          ...prev.specificFields,
          [fieldName]: value,
        },
      }));
    } else if (name === 'contactPhone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else if (name === 'revenueType') {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        annualRevenue: '',
        annualExpense: ''
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (fieldName, value) => {
    const currentValues = formData.specificFields[fieldName] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    setFormData((prev) => ({
      ...prev,
      specificFields: {
        ...prev.specificFields,
        [fieldName]: newValues,
      },
    }));
  };

  // Handle phone input to restrict invalid characters
  const handlePhoneInput = (e) => {
    const value = e.target.value;
    if (!/^\+?\d*\s*\d*\s*$/.test(value)) {
      e.target.value = value.replace(/(?!^\+)[^0-9\s]/g, '');
    }
  };

  // Handle image removal
  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.subcategory) newErrors.subcategory = 'Subcategory is required';
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    if (!formData.price) newErrors.price = 'Price is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.contactPhone) {
      newErrors.contactPhone = 'Contact phone is required';
    } else {
      const cleanedPhone = cleanPhoneNumber(formData.contactPhone);
      if (!/^\+?[0-9]{9,15}$/.test(cleanedPhone)) {
        newErrors.contactPhone = 'Please enter a valid phone number (e.g., +254 712 345 678)';
      }
    }
    if (formData.images.length === 0) newErrors.images = 'At least one image is required';

    // Validate specific fields
    const fields = getSpecificFields();
    fields.forEach((field) => {
      if (field.type !== 'multi-select') {
        if (field.required && !formData.specificFields[field.name]) {
          newErrors[`specificFields.${field.name}`] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      // Upload images to Firebase Storage
      const imageUrls = await Promise.all(
        formData.images.map(async (image) => {
          const imageRef = ref(storage, `listings/${uuidv4()}-${image.name}`);
          await uploadBytes(imageRef, image);
          return await getDownloadURL(imageRef);
        })
      );

      // Upload ZIP file if exists
      let zipUrl = null;
      if (formData.zipFile) {
        const zipRef = ref(storage, `listings/zips/${uuidv4()}-${formData.zipFile.name}`);
        await uploadBytes(zipRef, formData.zipFile);
        zipUrl = await getDownloadURL(zipRef);
      }

      // Prepare listing data
      const listingData = {
        category: formData.category,
        subcategory: formData.subcategory,
        title: formData.title,
        description: formData.description,
        currency: formData.currency,
        price: Number(formData.price),
        country: formData.country,
        address: formData.address,
        contactPhone: cleanPhoneNumber(formData.contactPhone),
        imageUrls,
        zipFileUrl: zipUrl,
        zipFileName: formData.zipFile?.name,
        revenueType: formData.revenueType,
        annualRevenue: formData.annualRevenue,
        annualExpense: formData.annualExpense,
        specificFields: formData.specificFields,
        userRef: auth.currentUser.uid,
        viewCount: formData.viewCount,
        timestamp: serverTimestamp(),
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'listings'), listingData);
      toast.success('Listing uploaded successfully');
      navigate(`/listing/${docRef.id}`);
    } catch (error) {
      console.error('Error uploading listing:', error);
      toast.error('Failed to upload listing');
    } finally {
      setLoading(false);
    }
  };

  // Get specific fields for the selected category
  const getSpecificFields = () => {
    if (!formData.category) return [];
    return specificFieldsConfig[formData.category] || [];
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8 text-center">Upload Digital Product</h1>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar: Category & Subcategory */}
          <div className="lg:col-span-1 bg-gray-800 shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Category Selection</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-200">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                      subcategory: '',
                      specificFields: {},
                    }));
                    setErrors((prev) => ({ ...prev, category: '' }));
                  }}
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                >
                  <option value="">Select Category</option>
                  {Object.keys(categories).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
              </div>
              {formData.category && (
                <div>
                  <label className="block text-sm font-medium text-gray-200">Subcategory</label>
                  <select
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        subcategory: e.target.value,
                        specificFields: {},
                      }));
                      setErrors((prev) => ({ ...prev, subcategory: '' }));
                    }}
                    className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                  >
                    <option value="">Select Subcategory</option>
                    {categories[formData.category].map((subcat) => (
                      <option key={subcat} value={subcat}>{subcat}</option>
                    ))}
                  </select>
                  {errors.subcategory && <p className="mt-1 text-sm text-red-500">{errors.subcategory}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Main Form */}
          <div className="lg:col-span-2 bg-gray-800 shadow-md rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Product Details</h2>
            <div className="space-y-6">
              {/* General Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-200">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                />
                {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                ></textarea>
                {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                  >
                    {currencies.map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code} - {curr.name}
                      </option>
                    ))}
                  </select>
                  {errors.currency && <p className="mt-1 text-sm text-red-500">{errors.currency}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200">Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                  />
                  {errors.price && <p className="mt-1 text-sm text-red-500">{errors.price}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200">Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.country && <p className="mt-1 text-sm text-red-500">{errors.country}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200">Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Address"
                  required
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                ></textarea>
                {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200">WhatsApp Contact Phone</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  onInput={handlePhoneInput}
                  placeholder="+256 761 832 669"
                  pattern="\+?[0-9\s]{9,15}"
                  required
                  className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 pl-4 text-base bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff]"
                />
                {errors.contactPhone && <p className="mt-1 text-sm text-red-500">{errors.contactPhone}</p>}
              </div>

              {/* Brand Startups Revenue Fields */}
              {formData.category === 'Brand Startups' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-200">Revenue Type</label>
                    <select
                      name="revenueType"
                      value={formData.revenueType}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                    >
                      <option value="">Select Revenue Type</option>
                      <option value="Pre-revenue">Pre-revenue</option>
                      <option value="Revenue-generating">Revenue-generating</option>
                    </select>
                  </div>
                  
                  {formData.revenueType === 'Revenue-generating' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-200">Annual Revenue Estimation ($)</label>
                        <input
                          type="number"
                          name="annualRevenue"
                          value={formData.annualRevenue}
                          onChange={handleChange}
                          className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-200">Annual Expense Estimation ($)</label>
                        <input
                          type="number"
                          name="annualExpense"
                          value={formData.annualExpense}
                          onChange={handleChange}
                          className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ZIP File Upload (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Upload ZIP Folder <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".zip,application/zip"
                    onChange={handleZipUpload}
                    className="hidden"
                    id="zip-upload"
                  />
                  <label
                    htmlFor="zip-upload"
                    className="block cursor-pointer"
                  >
                    {!zipUploadState.file ? (
                      <div className="border-2 border-dashed border-gray-600 rounded-xl p-8 bg-gray-900 hover:border-[#4da3ff] transition-colors">
                        <div className="flex flex-col items-center justify-center">
                          <div className="bg-gray-800 p-4 rounded-full mb-4">
                            <FolderZip className="h-12 w-12 text-gray-400" />
                          </div>
                          <p className="text-gray-300 font-medium mb-1">Click to upload ZIP folder</p>
                          <p className="text-gray-500 text-sm">Maximum file size: 100MB</p>
                        </div>
                      </div>
                    ) : (
                      <div className={`border-2 rounded-xl p-6 ${
                        zipUploadState.error ? 'border-red-500 bg-red-900/20' :
                        zipUploadState.success ? 'border-blue-500 bg-blue-900/20' :
                        'border-gray-600 bg-gray-900'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <FolderZip className={`h-8 w-8 ${
                              zipUploadState.error ? 'text-red-500' :
                              zipUploadState.success ? 'text-blue-500' :
                              'text-gray-400'
                            }`} />
                            <div>
                              <p className="text-white font-medium">{zipUploadState.file.name}</p>
                              <p className="text-gray-400 text-sm">
                                {(zipUploadState.file.size / (1024 * 1024)).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          {zipUploadState.success && (
                            <button
                              type="button"
                              onClick={removeZipFile}
                              className="text-gray-400 hover:text-white"
                            >
                              ×
                            </button>
                          )}
                        </div>

                        {zipUploadState.uploading && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-500">Uploading...</span>
                              <span className="text-blue-500">{zipUploadState.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${zipUploadState.progress}%` }}
                              />
                            </div>
                          </div>
                        )}

                        {zipUploadState.error && (
                          <div className="flex items-center space-x-2 text-red-500 mt-2">
                            <Error className="h-5 w-5" />
                            <span className="text-sm">{zipUploadState.error}</span>
                          </div>
                        )}

                        {zipUploadState.success && !zipUploadState.uploading && (
                          <div className="flex items-center space-x-2 text-blue-500 mt-2">
                            <CheckCircle className="h-5 w-5" />
                            <span className="text-sm">Upload successful!</span>
                          </div>
                        )}
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-200">Images (Max 50, 5MB each)</label>
                <input
                  type="file"
                  name="images"
                  multiple
                  accept="image/*"
                  onChange={handleChange}
                  className="mt-1 block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                />
                {errors.images && <p className="mt-1 text-sm text-red-500">{errors.images}</p>}
                <div className="mt-4 grid grid-cols-3 gap-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index}`}
                        className="h-24 w-full object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category-Specific Fields */}
              {formData.subcategory && getSpecificFields().length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Technical Details</h3>
                  <div className="space-y-4">
                    {getSpecificFields().map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-200">{field.label}</label>
                        {field.type === 'select' ? (
                          <select
                            name={`specificFields.${field.name}`}
                            value={formData.specificFields[field.name] || ''}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                          >
                            <option value="">Select {field.label}</option>
                            {field.options.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : field.type === 'multi-select' ? (
                          <div className="mt-2 space-y-2 max-h-48 overflow-y-auto p-2 border border-gray-600 rounded-md">
                            {field.options.map((option) => (
                              <label key={option} className="flex items-center space-x-2 text-gray-200">
                                <input
                                  type="checkbox"
                                  value={option}
                                  checked={(formData.specificFields[field.name] || []).includes(option)}
                                  onChange={(e) => handleMultiSelectChange(field.name, option)}
                                  className="rounded border-gray-600 bg-gray-800 text-[#4da3ff] focus:ring-[#4da3ff]"
                                />
                                <span className="text-sm">{option}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            name={`specificFields.${field.name}`}
                            value={formData.specificFields[field.name] || ''}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 bg-gray-800 text-white focus:outline-none focus:ring-[#4da3ff] focus:border-[#4da3ff] sm:text-sm"
                          />
                        )}
                        {errors[`specificFields.${field.name}`] && (
                          <p className="mt-1 text-sm text-red-500">{errors[`specificFields.${field.name}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4da3ff]"
              >
                Upload Product
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}